import type { TableDefinition } from './definition';

/**
 * Grand Circuit is the default reference machine: deliberately readable,
 * conventional and easy to remix. The OUTER boundary is a true rectangle;
 * curves/arches live inside the cabinet as guides like a physical machine.
 * Its flagship art direction is the Balls of Pin / Pin neon-noir identity.
 */
export const GRAND_CIRCUIT: TableDefinition = {
  schemaVersion: 1,
  id: 'grand-circuit-v1',
  name: 'Grand Circuit',
  authorInitials: 'BOP',
  provenance: { isOfficial: true, isPublished: true },
  board: {
    width: 52,
    height: 104,
    inclineDeg: 6.4,
    plungerMaxPower: 200,
    ballsPerGame: 3,
    ballSaverSeconds: 12,
    nudgeCount: 3,
  },
  theme: {
    presetId: 'synthwave-drive',
    visualPackId: 'synthwave-drive',
    accentColor: '#ff3eaa',
    secondaryColor: '#37d9ff',
    playfieldColor: '#071b3d',
    playfieldSecondaryColor: '#102f62',
    cabinetColor: '#05060d',
    cabinetEdgeColor: '#35112e',
    metalColor: '#e0edf7',
    rubberColor: '#f4d7e7',
    flipperColor: '#ff78b5',
    backgroundColor: '#02030a',
    playfieldPattern: 'stars',
    soundPackId: 'classic-mechanical',
    lightingPreset: 'neon',
    artSlots: {
      'playfield-center': 'pin-moon',
      apron: 'pin-claw',
      'bumper-cap': 'pin-paw',
      'target-face': 'pin-paw',
      'ramp-decal': 'pin-claw',
      'sling-plastic': 'pin-claw',
    },
  },
  parts: [
    {
      id: 'wall-left', kind: 'wall', scoreValue: 0, thickness: 1.1, material: 'metal',
      points: [{ x: 2, y: 2 }, { x: 2, y: 99 }],
    },
    {
      id: 'wall-top', kind: 'wall', scoreValue: 0, thickness: 1.1, material: 'metal',
      points: [{ x: 2, y: 2 }, { x: 50, y: 2 }],
    },
    {
      id: 'wall-right', kind: 'wall', scoreValue: 0, thickness: 1.1, material: 'metal',
      points: [{ x: 50, y: 2 }, { x: 50, y: 99 }],
    },
    {
      id: 'plunger-lane',
      kind: 'plunger-lane',
      scoreValue: 0,
      material: 'metal',
      bounds: { x: 45.5, y: 7, width: 4.5, height: 93 },
      restPosition: { x: 48, y: 96 },
      divider: { points: [{ x: 45.5, y: 18 }, { x: 45.5, y: 97 }], thickness: 0.8 },
      stop: { center: { x: 48, y: 100 }, width: 4, thickness: 1 },
      launchPath: [
        { x: 48, y: 96 },
        { x: 48, y: 27 },
        { x: 48, y: 9 },
        { x: 45.5, y: 5 },
        { x: 41, y: 6.5 },
        { x: 38, y: 11 },
      ],
    },
    {
      id: 'shooter-return-guide',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.55,
      collider: true,
      material: 'metal',
      points: [
        { x: 45.2, y: 18 }, { x: 45, y: 10 }, { x: 42.5, y: 6.5 }, { x: 38, y: 8.5 },
      ],
    },
    {
      id: 'grand-shooter-return-continuation',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.9,
      collider: true,
      material: 'metal',
      color: '#37d9ff',
      // Close the narrow shelf under the shooter divider by joining directly
      // into the existing right-outlane mouth. This forms one sloped boundary
      // instead of a second parallel rail that could create another ball-width trap.
      points: [{ x: 45, y: 64 }, { x: 43.5, y: 70 }],
    },
    { id: 'roll-a', kind: 'rollover', scoreValue: 250, center: { x: 17, y: 13 }, radius: 1.25, laneId: 'top-lanes', color: '#ff3eaa' },
    { id: 'roll-b', kind: 'rollover', scoreValue: 250, center: { x: 26, y: 13 }, radius: 1.25, laneId: 'top-lanes', color: '#ffd45c' },
    { id: 'roll-c', kind: 'rollover', scoreValue: 250, center: { x: 35, y: 13 }, radius: 1.25, laneId: 'top-lanes', color: '#37d9ff' },
    {
      id: 'top-divider-left', kind: 'guide-rail', scoreValue: 0, thickness: 0.45, collider: true,
      points: [{ x: 21.5, y: 8.5 }, { x: 21.5, y: 18.5 }], material: 'metal',
    },
    {
      id: 'top-divider-right', kind: 'guide-rail', scoreValue: 0, thickness: 0.45, collider: true,
      points: [{ x: 30.5, y: 8.5 }, { x: 30.5, y: 18.5 }], material: 'metal',
    },
    // The flagship table keeps a readable three-pop cluster, but each contact now
    // has a different recovery character: left settles the ball, center remains the
    // neutral reference, and right kicks hard enough to demand an immediate read.
    // Every themed clone overrides bumper impulse, so this tuning stays Grand-only.
    { id: 'bumper-left', kind: 'bumper', scoreValue: 500, center: { x: 19, y: 29 }, radius: 3.2, impulse: 46, color: '#ff3eaa' },
    { id: 'bumper-right', kind: 'bumper', scoreValue: 500, center: { x: 33, y: 29 }, radius: 3.2, impulse: 74, color: '#37d9ff' },
    { id: 'bumper-center', kind: 'bumper', scoreValue: 750, center: { x: 26, y: 40 }, radius: 3.35, impulse: 62, color: '#ffd45c' },
    {
      id: 'left-orbit-outer', kind: 'guide-rail', scoreValue: 0, thickness: 0.6, collider: true, material: 'metal',
      points: [{ x: 6, y: 65 }, { x: 5.2, y: 49 }, { x: 6, y: 32 }, { x: 9, y: 20 }, { x: 13, y: 10 }],
    },
    {
      id: 'left-orbit-inner', kind: 'guide-rail', scoreValue: 0, thickness: 0.5, collider: true, material: 'metal',
      points: [{ x: 10.5, y: 61 }, { x: 10, y: 48 }, { x: 11, y: 35 }, { x: 14, y: 23 }],
    },
    { id: 'target-1', kind: 'target', scoreValue: 600, center: { x: 17, y: 57 }, width: 3.4, dropDown: false, color: '#ff3eaa' },
    { id: 'target-2', kind: 'target', scoreValue: 600, center: { x: 24, y: 56 }, width: 3.4, dropDown: false, color: '#ffd45c' },
    { id: 'target-3', kind: 'target', scoreValue: 600, center: { x: 31, y: 57 }, width: 3.4, dropDown: false, color: '#37d9ff' },
    {
      id: 'ramp-main',
      kind: 'ramp',
      scoreValue: 1800,
      material: 'plastic',
      entry: { x: 37, y: 67 },
      path: [
        { x: 37, y: 67 },
        { x: 39.5, y: 58 },
        { x: 40, y: 47 },
        { x: 39.5, y: 36 },
        { x: 37.5, y: 26 },
        { x: 34, y: 21 },
      ],
      exit: { x: 34, y: 21 },
      exitVelocity: { x: -7, y: 18 },
      color: '#163d73',
    },
    {
      id: 'guide-left-outlane', kind: 'guide-rail', scoreValue: 0, thickness: 0.7, collider: true, material: 'metal',
      points: [{ x: 5, y: 70 }, { x: 5.2, y: 86 }, { x: 11.5, y: 98 }],
    },
    {
      id: 'guide-left-inlane', kind: 'guide-rail', scoreValue: 0, thickness: 0.7, collider: true, material: 'metal',
      points: [{ x: 13, y: 74 }, { x: 14, y: 86 }, { x: 18, y: 91.5 }],
    },
    {
      id: 'guide-right-inlane', kind: 'guide-rail', scoreValue: 0, thickness: 0.7, collider: true, material: 'metal',
      points: [{ x: 39, y: 74 }, { x: 38, y: 86 }, { x: 34, y: 91.5 }],
    },
    {
      id: 'guide-right-outlane', kind: 'guide-rail', scoreValue: 0, thickness: 0.7, collider: true, material: 'metal',
      points: [{ x: 43.5, y: 70 }, { x: 43.2, y: 86 }, { x: 40.5, y: 98 }],
    },
    {
      id: 'sling-left', kind: 'slingshot', scoreValue: 100, color: '#ff3eaa',
      points: [{ x: 12, y: 76 }, { x: 19.2, y: 83 }, { x: 12.5, y: 89 }],
    },
    {
      id: 'sling-right', kind: 'slingshot', scoreValue: 100, color: '#37d9ff',
      points: [{ x: 40, y: 76 }, { x: 32.8, y: 83 }, { x: 39.5, y: 89 }],
    },
    { id: 'post-left-outer', kind: 'post', scoreValue: 0, center: { x: 11, y: 75.5 }, radius: 0.9, collider: true, material: 'rubber' },
    { id: 'post-left-inner', kind: 'post', scoreValue: 0, center: { x: 19.2, y: 83 }, radius: 0.9, collider: true, material: 'rubber' },
    { id: 'post-right-inner', kind: 'post', scoreValue: 0, center: { x: 32.8, y: 83 }, radius: 0.9, collider: true, material: 'rubber' },
    { id: 'post-right-outer', kind: 'post', scoreValue: 0, center: { x: 41, y: 75.5 }, radius: 0.9, collider: true, material: 'rubber' },
    {
      id: 'flip-l', kind: 'flipper-left', scoreValue: 0,
      pivot: { x: 17, y: 95 }, length: 8, restAngleDeg: 30, sweepDeg: 56, color: '#ff78b5',
    },
    {
      id: 'flip-r', kind: 'flipper-right', scoreValue: 0,
      pivot: { x: 35, y: 95 }, length: 8, restAngleDeg: 30, sweepDeg: 56, color: '#ff78b5',
    },
    { id: 'insert-orbit', kind: 'insert', scoreValue: 0, center: { x: 9, y: 67 }, shape: 'arrow', width: 2.2, height: 4.2, rotationDeg: 180, color: '#ff3eaa', emissive: 1.45 },
    { id: 'insert-ramp', kind: 'insert', scoreValue: 0, center: { x: 37, y: 70 }, shape: 'arrow', width: 2.2, height: 4.2, rotationDeg: 180, color: '#37d9ff', emissive: 1.45 },
    { id: 'insert-center', kind: 'insert', scoreValue: 0, center: { x: 26, y: 49 }, shape: 'diamond', width: 2.4, height: 2.4, color: '#ffd45c', emissive: 1.5 },
    { id: 'drain', kind: 'drain', scoreValue: 0, center: { x: 26, y: 102.5 }, width: 13 },
  ],
};